import {
  Accordion,
  UserGroup,
  Shipping,
  CarrierType,
  TOGGLE_FEATURE,
  SearchBar,
} from "@jasper/shared";

import { UserGroupShipping } from "./UserGroupShipping";
import { Button, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { UseAuth } from "/@/contexts";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useDebounce } from "use-debounce";

interface DisplayShippingProps {
  data: UserGroup[];
  opened: string[];
  setOpened: (opened: string[]) => void;
  downloadShippingLabel: (userGroup: UserGroup) => Shipping[];
  carrier: CarrierType;
  downloadAllLabels: () => void;
  markAllAsShipped: () => void;
  getTodayShippingByDeliveryCarrier: (
    deliveryCarrier: CarrierType,
    skip: number,
    filters: {
      orderReference?: string;
      clinicNumber?: string;
      patientFirstname?: string;
      patientLastname?: string;
    }
  ) => Promise<UserGroup[]>;
  paginationNumber: number;
  isTodayDateSelected: boolean;
  totalCount: number;
  fileCount: number;
  setSearchText: (searchText: string) => void;
  searchText: string;
}

const DisplayShipping = ({
  data,
  opened,
  setOpened,
  downloadShippingLabel,
  markAllAsShipped,
  carrier,
  downloadAllLabels,
  getTodayShippingByDeliveryCarrier,
  paginationNumber,
  isTodayDateSelected,
  totalCount,
  fileCount,
  setSearchText,
  searchText,
}: DisplayShippingProps) => {
  const { user } = UseAuth();
  const bipDeliveryEnabled = useFeatureFlagEnabled("bip_delivery") ?? false;
  const [searchInputDebounce] = useDebounce(searchText, 500);

  const showDownloadAllLabelsButton =
    TOGGLE_FEATURE.INTEGRATED_DELIVERY_CARRIERS(user.email).includes(carrier) &&
    isTodayDateSelected &&
    (carrier !== CarrierType.BIP || bipDeliveryEnabled);

  const showMarkAllAsShippedButton =
    !showDownloadAllLabelsButton && isTodayDateSelected;

  const [userGroupShippings, setUserGroupShippings] = useState<UserGroup[]>([]);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(true);

  useEffect(() => {
    setUserGroupShippings(data);
    if (data?.length === 0) {
      setShowLoadMoreButton(false);
    }
  }, [JSON.stringify(data), paginationNumber]);

  useEffect(() => {
    const fetchNewShippingWithSearch = async () => {
      const req = await getTodayShippingByDeliveryCarrier(carrier, 0, {
        orderReference: searchInputDebounce,
        clinicNumber: searchInputDebounce,
        patientFirstname: searchInputDebounce,
        patientLastname: searchInputDebounce,
      });
      setUserGroupShippings(req);
      setShowLoadMoreButton(req.length >= paginationNumber);
    };
    fetchNewShippingWithSearch();
  }, [searchInputDebounce]);

  return (
    <>
      <div style={{ paddingTop: "1rem", paddingBottom: "0.5rem" }}>
        <SearchBar
          setSearchText={value => {
            setSearchText(value);
          }}
          searchText={searchText}
          placeholder="Search by firstname or lastname of patient, order ref, or clinic code"
        />
      </div>
      <Text>{`${totalCount} labels in ${fileCount} file(s)`}</Text>
      {showDownloadAllLabelsButton && (
        <div style={{ textAlign: "center", padding: "1rem" }}>
          <Button onClick={downloadAllLabels}>
            Download all {carrier} labels
          </Button>
        </div>
      )}

      {showMarkAllAsShippedButton && (
        <div style={{ textAlign: "center", padding: "1rem" }}>
          <Button onClick={markAllAsShipped}>
            Mark all {carrier} as shipped
          </Button>
        </div>
      )}

      {(userGroupShippings ?? []).map((userGroup: UserGroup) => {
        const title = `${userGroup?.clinicNumber} - ${userGroup?.displayName}`;
        return (
          <Accordion
            key={title}
            setOpened={(title: string) => {
              if (opened.includes(title)) {
                setOpened([...opened.filter(prev => prev !== title)]);
                return;
              }
              setOpened([...opened, title]);
            }}
            opened={opened}
            items={[
              {
                value: title,
                content: (
                  <UserGroupShipping
                    userGroup={userGroup}
                    isTodayDateSelected={isTodayDateSelected}
                    downloadShippingLabel={() => {
                      return downloadShippingLabel(userGroup);
                    }}
                  />
                ),
              },
            ]}
          />
        );
      })}
      {!showLoadMoreButton && (
        <Text style={{ padding: "1rem", textAlign: "center" }}>
          No more shipping to display without re-computing
        </Text>
      )}
      {userGroupShippings.length % paginationNumber === 0 &&
        showLoadMoreButton && (
          <div style={{ textAlign: "center", paddingTop: "2rem" }}>
            <Button
              onClick={async () => {
                const req = await getTodayShippingByDeliveryCarrier(
                  carrier,
                  userGroupShippings?.length,
                  {
                    orderReference: searchText,
                    clinicNumber: searchText,
                    patientFirstname: searchText,
                    patientLastname: searchText,
                  }
                );
                setUserGroupShippings([...userGroupShippings, ...(req ?? [])]);
                if ((req ?? []).length < paginationNumber) {
                  setShowLoadMoreButton(false);
                }
              }}
            >
              Load more
            </Button>
          </div>
        )}
    </>
  );
};

export default DisplayShipping;
